import React, { useState, useEffect } from 'react';
import { Alert, Button, Dropdown, Form } from 'react-bootstrap';
import { getAllStates, getAllCities } from '../../../../../crud/configuration_api/places';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import './CreateLocal.scss';

interface CreateLocalProps {
  index: number;
  moveFunctions: { next: () => void; back: () => void };
  setPayload: (data: any) => void;
  accountCode: string;
  payload: any;
  states: any[];
  setStates: React.Dispatch<React.SetStateAction<any[]>>;
  cities: any[];
  setCities: React.Dispatch<React.SetStateAction<any[]>>;
}

const CreateLocal: React.FC<CreateLocalProps> = ({ index, moveFunctions, setPayload, accountCode, payload, states, setStates, cities, setCities }) => {
  const storedData = JSON.parse(localStorage.getItem('RegistrationMemory') || '[]');
  const companyData = storedData[0] || {}; // Datos de la compañía
  const localData = storedData[1] || {}; // Datos del local
  const [error, setError] = useState<string | null>(null);

  // Inicializar formData con payload si existe
  const initialFormData = payload || {
    local_name: companyData.company_name || '',
    address: localData.address || '',
    city_id: companyData.city_id || localData.state_local || '',
    state_local: companyData.estado || localData.state_local || '',
    company_id: companyData.company_id || '',
    code: accountCode,
  };
  const [formData, setFormData] = useState(initialFormData);

  // Sincronizar formData cuando cambie el payload
  useEffect(() => {
    if (payload) setFormData(payload);
  }, [payload]);

  useEffect(() => {
    getAllStates()
      .then((response) => {
        setStates(response.data);
        if (formData.state_local) {
          const selectedState = response.data.find((state: any) => state.state_id.toString() === formData.state_local);
          if (selectedState) {
            setFormData((prev: any) => ({ ...prev, state_local: selectedState.state_id.toString() }));
          }
        }
      })
      .catch((error: any) => console.error('Error al obtener estados:', error));
  }, []);

  useEffect(() => {
    if (formData.state_local) {
      getAllCities()
        .then((response) => {
          const filteredCities = response.data.filter(
            (city: any) => city.state_id.toString() === formData.state_local
          );
          setCities(filteredCities);
          if (formData.city_id) {
            const selectedCity = filteredCities.find((city: any) => city.city_id.toString() === formData.city_id);
            if (selectedCity) {
              setFormData((prev: any) => ({ ...prev, city_id: selectedCity.city_id.toString() }));
            }
          }
        })
        .catch((error: any) => console.error('Error al obtener ciudades:', error));
    } else {
      setCities([]);
    }
  }, [formData.state_local]);

  const handleSelectChange = (field: string, value: string) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
    if (field === 'state_local') {
      setFormData((prev: any) => ({ ...prev, city_id: '' }));
    }
  };

  const validateForm = () => {
    // Verifica si todos los campos requeridos están completos
    if (!formData.local_name || !formData.address || !formData.state_local || !formData.city_id) {
      setError('Todos los campos son requeridos.');
      return false;
    }

    setError(null);
    return true;
  };

  const handleNext = () => {
    if (validateForm()) {
      const updatedData = { ...formData, code: accountCode };
      setPayload(updatedData);
      // Guardar el local en localStorage en la posición [1] de RegistrationMemory
      const mem = localStorage.getItem('RegistrationMemory');
      const parsedMem = mem ? JSON.parse(mem) : {};
      parsedMem[1] = { ...parsedMem[1], ...updatedData };
      localStorage.setItem('RegistrationMemory', JSON.stringify(parsedMem));
      moveFunctions.next();
    }
  };

  return (
    <div>
      <div className="logo-container-preg">
        <img
          src="/logos/Recurso 40MEDIOPUNTO.png"
          alt="Logo Mediopunto"
          className="registration-logo-preg"
        />
      </div>
      <div className="info-guide-preg">
        <h2 className="Title-Step-Registration-preg">Crea un local</h2>
        <p>
          <FontAwesomeIcon
            icon={faInfoCircle}
            size="sm"
            className="info-icon"
          />{' '}
¡Genial! Ya registraste tu compañía con éxito. El siguiente paso es añadir tu primer local.
Por favor, completa toda la información del local en los campos que aparecen a continuación (asegúrate de no dejar ningún campo vacío).
        </p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Nombre del Local */}
      <div className='form-create-local'>
        <Form.Group>
          <Form.Control
            type="text"
            id="local_name"
            name="local_name"
            value={formData.local_name}
            onChange={(e) => handleSelectChange('local_name', e.target.value)}
            required
            placeholder='Nombre del Local'
            className='input-create-local'  
          />
        </Form.Group>

      {/* Dirección */}
        <Form.Control
          type="text"
          id="address"
          name="address"
          value={formData.address}
          onChange={(e) => handleSelectChange('address', e.target.value)}
          required
          placeholder='Dirección'
          className='input-create-local'  
        />
        <Dropdown className='dropdown-create-local'>
          <Dropdown.Toggle variant="none">
            {formData.state_local
              ? states.find((s) => s.state_id.toString() === formData.state_local)?.state_name || 'Seleccione una región'
              : 'Seleccione una región'}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {states.map((state) => (
              <Dropdown.Item
                key={state.state_id}
                onClick={() => handleSelectChange('state_local', state.state_id.toString())}
              >
                {state.state_name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        {formData.state_local && (
          <Dropdown id="dropdown-city-id" className='dropdown-create-local'>
            <Dropdown.Toggle variant="outline-secondary">
              {formData.city_id
                ? cities.find((c) => c.city_id.toString() === formData.city_id)?.city_name || 'Seleccione una ciudad'
                : 'Seleccione una ciudad'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {cities.map((city) => (
                <Dropdown.Item
                  key={city.city_id}
                  onClick={() => handleSelectChange('city_id', city.city_id.toString())}
                >
                  {city.city_name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
      

      {/* Botones de navegación */}
      <div className="button-group">
        <Button className='BackStep_Registration-preg' variant='none' onClick={moveFunctions.back}><ChevronLeft/> Anterior  </Button>
        <Button className='NextStep_Registration-preg' variant='none' onClick={handleNext}>Siguiente <ChevronRight/></Button>
      </div>
    </div>
  );
};

export default CreateLocal;
//LISTO OF//